/*
 * Layout viewport metrics and CSS synchronization across resizing and browser zoom.
 * Started by the application shell.
 * Start once for the page and call the returned cleanup function when its lifetime ends.
 * Example: const stop = startLayoutViewportSync(window, document.documentElement, transition);
 * On owner disposal: stop();
 */

const BASE_DPR_KEY = 'layout.base-device-pixel-ratio';
export const FULL_DESKTOP_MIN_WIDTH = 1440;

export function layoutViewportMetrics(innerWidth, devicePixelRatio, baseDevicePixelRatio) {
  const inner = Number(innerWidth);
  const dpr = Number(devicePixelRatio);
  const baseDpr = Number(baseDevicePixelRatio);
  if (
    !Number.isFinite(inner) ||
    inner <= 0 ||
    !Number.isFinite(dpr) ||
    dpr <= 0 ||
    !Number.isFinite(baseDpr) ||
    baseDpr <= 0
  ) {
    return { zoom: 1, width: Math.max(0, Number.isFinite(inner) ? inner : 0) };
  }

  const zoom = dpr / baseDpr;
  return { zoom, width: inner * zoom };
}

export function startLayoutViewportSync(viewport, root, transition) {
  const touchLayout = viewport.matchMedia?.('(hover: none) and (pointer: coarse)');
  let baseDpr;
  try {
    baseDpr = Number(viewport.sessionStorage.getItem(BASE_DPR_KEY));
  } catch {
    baseDpr = 0;
  }
  let resetBaseline = false,
    previousLayout = null,
    pendingLayout = false,
    latest = null,
    stopped = false;

  const apply = () => {
    if (stopped) return;
    const { zoom, width } = latest;
    root.style.setProperty('--layout-zoom', String(zoom));
    root.classList.toggle('layout-sm', width >= 640);
    root.classList.toggle('layout-lg', width >= FULL_DESKTOP_MIN_WIDTH);
  };

  const sync = () => {
    if (stopped) return;
    if (!touchLayout?.matches && (resetBaseline || !Number.isFinite(baseDpr) || baseDpr <= 0)) {
      baseDpr = viewport.devicePixelRatio || 1;
      try {
        viewport.sessionStorage.setItem(BASE_DPR_KEY, String(baseDpr));
      } catch {
        /* storage unavailable */
      }
      resetBaseline = false;
    }
    // Mobile overflow can keep innerWidth wider than the viewport after rotation.
    const clientWidth = root.clientWidth;
    const width =
      Number.isFinite(clientWidth) && clientWidth > 0 ? clientWidth : viewport.innerWidth;
    // Touch layouts follow their CSS viewport. A desktop DPR baseline can belong
    // to a different display or DevTools profile and must not enlarge a phone.
    latest = touchLayout?.matches
      ? layoutViewportMetrics(width, 1, 1)
      : layoutViewportMetrics(width, viewport.devicePixelRatio, baseDpr);
    const layout =
      latest.width < 640 ? 'phone' : latest.width < FULL_DESKTOP_MIN_WIDTH ? 'tablet' : 'desktop';
    const changed = previousLayout !== null && previousLayout !== layout;
    previousLayout = layout;
    if (pendingLayout) return;
    if (changed && root.ownerDocument?.startViewTransition) {
      pendingLayout = true;
      void transition(() => {
        apply();
        pendingLayout = false;
      });
    } else apply();
  };

  const onKeyDown = (event) => {
    if (!touchLayout?.matches && (event.metaKey || event.ctrlKey) && event.key === '0')
      resetBaseline = true;
  };

  sync();
  viewport.addEventListener('resize', sync, { passive: true });
  viewport.addEventListener('keydown', onKeyDown);
  touchLayout?.addEventListener?.('change', sync);

  return () => {
    stopped = true;
    viewport.removeEventListener('resize', sync);
    viewport.removeEventListener('keydown', onKeyDown);
    touchLayout?.removeEventListener?.('change', sync);
  };
}
